import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { Database, X, ArrowRight, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Settings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [thresholds, setThresholds] = useState({ cushion_tight: "", cushion_short: "" });
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [effectiveThresholds, setEffectiveThresholds] = useState({ effective_tight: 100, effective_short: 0 });
  // Monthly Summary — opt-in stored on the user; delivery via Resend (not yet wired).
  const [monthlySummaryOptIn, setMonthlySummaryOptIn] = useState(false);
  const [monthlySummaryEmail, setMonthlySummaryEmail] = useState("");
  const [savingMonthlySummary, setSavingMonthlySummary] = useState(false);
  // Weekly Balance Reminder — Monday-morning opt-in email nudge. Off by default.
  const [weeklyReminderOptIn, setWeeklyReminderOptIn] = useState(false);
  const [savingWeeklyReminder, setSavingWeeklyReminder] = useState(false);
  // Snapshot Autoshare — remembered email address for the Dashboard "Email
  // snapshot" one-tap button. Editable here so users can point future
  // sends at a shared inbox / a partner's inbox without re-typing.
  const [autoshareEmail, setAutoshareEmail] = useState("");
  const [savingAutoshareEmail, setSavingAutoshareEmail] = useState(false);
  // Snapshot follow-up — 7-day "did it actually happen?" check-in email
  // to the user after they share a tight-week snapshot. Warm/celebratory
  // if they reconciled during the week, gentle nudge if not. Threshold is
  // the projected end-of-period balance ≤ which triggers the follow-up.
  const [followupOptIn, setFollowupOptIn] = useState(true);
  const [followupThreshold, setFollowupThreshold] = useState(0);
  const [savingFollowup, setSavingFollowup] = useState(false);
  // General Spending low-balance nudge threshold (Feb 2026 D3). When the
  // total balance across the user's General Spending accounts is at or
  // below this number, Step 1 shows a soft "running low" nudge.
  const [gsThreshold, setGsThreshold] = useState(100);
  const [savingGs, setSavingGs] = useState(false);
  // Demo-data status: how many untouched sample rows remain across the
  // user's collections. Drives whether the "Clear sample data" button is
  // enabled and what the sub-copy shows.
  const [demoStatus, setDemoStatus] = useState({ total: 0, by_collection: {} });
  const [clearingDemo, setClearingDemo] = useState(false);
  const [showClearDemoModal, setShowClearDemoModal] = useState(false);

  const load = async () => {
    setLoading(true);
    const [account, thr, demo] = await Promise.all([
      api.get("/account"),
      api.get("/settings/thresholds"),
      api.get("/onboarding/demo-status"),
    ]);
    setNotes(account.data.notes || "");
    setThresholds({
      cushion_tight: thr.data.cushion_tight ?? "",
      cushion_short: thr.data.cushion_short ?? "",
    });
    setEffectiveThresholds({
      effective_tight: thr.data.effective_tight,
      effective_short: thr.data.effective_short,
    });
    setMonthlySummaryOptIn(Boolean(user?.monthly_summary_opt_in));
    setMonthlySummaryEmail(user?.monthly_summary_email || user?.email || "");
    setWeeklyReminderOptIn(Boolean(user?.weekly_reminder_opt_in));
    setAutoshareEmail(user?.autoshare_email || "");
    setFollowupOptIn(user?.snapshot_followup_opt_in !== false);
    setFollowupThreshold(Number(user?.snapshot_followup_threshold ?? 0));
    setGsThreshold(Number(user?.general_spending_low_threshold ?? 100));
    setDemoStatus(demo.data);
    setLoading(false);
  };

  const clearDemoData = async () => {
    setClearingDemo(true);
    try {
      const { data } = await api.post("/onboarding/clear-demo");
      toast.success(`Removed ${data.total_deleted} untouched sample row${data.total_deleted === 1 ? "" : "s"}.`);
      try { window.localStorage.removeItem("wmrb_demo_loaded"); } catch { /* ignore */ }
      setShowClearDemoModal(false);
      // Refresh status so the button reflects the new state.
      const status = await api.get("/onboarding/demo-status");
      setDemoStatus(status.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClearingDemo(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveNotes = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/account", { notes });
      toast.success("Notes saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const loadDemo = async () => {
    setDemoLoading(true);
    try {
      await api.post("/onboarding/load-demo");
      track("demo_data_loaded", { from: "settings" });
      await refresh();
      toast.success("Sample data loaded — taking you to the dashboard");
      setShowDemoModal(false);
      setTimeout(() => navigate("/app/dashboard"), 400);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load demo data. Please try again.");
    } finally { setDemoLoading(false); }
  };

  const saveMonthlySummary = async (nextOptIn) => {
    setSavingMonthlySummary(true);
    try {
      await api.post("/settings/monthly-summary", {
        opt_in: nextOptIn,
        email: monthlySummaryEmail || user?.email || "",
      });
      setMonthlySummaryOptIn(nextOptIn);
      await refresh();
      toast.success(nextOptIn
        ? "Subscribed. We'll email you a monthly recap on the 1st."
        : "Unsubscribed. No more monthly recaps.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingMonthlySummary(false); }
  };

  const saveWeeklyReminder = async (nextOptIn) => {
    setSavingWeeklyReminder(true);
    try {
      await api.post("/settings/weekly-reminder", { opt_in: nextOptIn });
      setWeeklyReminderOptIn(nextOptIn);
      await refresh();
      toast.success(nextOptIn
        ? "You're in. Expect a Monday-morning check-in email each week."
        : "Weekly reminders off. You can turn them back on anytime.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingWeeklyReminder(false); }
  };

  const saveAutoshareEmail = async (e) => {
    e.preventDefault();
    setSavingAutoshareEmail(true);
    try {
      const trimmed = (autoshareEmail || "").trim();
      await api.post("/settings/autoshare-email", { email: trimmed });
      setAutoshareEmail(trimmed);
      await refresh();
      toast.success(trimmed
        ? "Snapshot email saved. One tap on the Dashboard sends here."
        : "Snapshot email cleared.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingAutoshareEmail(false); }
  };

  // Toggle only — threshold saves through a separate form submit so users
  // don't spam the API on every keystroke while typing the dollar amount.
  const saveFollowupOptIn = async (nextOptIn) => {
    setSavingFollowup(true);
    try {
      await api.post("/settings/snapshot-followup", { opt_in: nextOptIn });
      setFollowupOptIn(nextOptIn);
      await refresh();
      toast.success(nextOptIn
        ? "Follow-ups on. You'll get a warm check-in a week after tight-week snapshots."
        : "Follow-ups off. You can turn them back on anytime.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingFollowup(false); }
  };

  const saveFollowupThreshold = async (e) => {
    e.preventDefault();
    setSavingFollowup(true);
    try {
      const num = Number(followupThreshold);
      if (!Number.isFinite(num)) throw new Error("Enter a valid amount");
      await api.post("/settings/snapshot-followup", { threshold: num });
      await refresh();
      toast.success("Follow-up threshold saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingFollowup(false); }
  };

  const saveGsThreshold = async (e) => {
    e.preventDefault();
    setSavingGs(true);
    try {
      const num = Number(gsThreshold);
      if (!Number.isFinite(num)) throw new Error("Enter a valid amount");
      await api.post("/settings/general-spending-threshold", { threshold: num });
      await refresh();
      toast.success("General Spending threshold saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingGs(false); }
  };

  const saveThresholds = async (e) => {
    e.preventDefault();
    setSavingThresholds(true);
    try {
      const body = {
        cushion_tight: thresholds.cushion_tight === "" ? null : Number(thresholds.cushion_tight),
        cushion_short: thresholds.cushion_short === "" ? null : Number(thresholds.cushion_short),
      };
      const { data } = await api.put("/settings/thresholds", body);
      setEffectiveThresholds({
        effective_tight: data.effective_tight,
        effective_short: data.effective_short,
      });
      toast.success("Cushion thresholds saved. Dashboard will reflect the new labels on next refresh.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingThresholds(false); }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Settings</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Your account & preferences</h1>
      </div>

      <div className="card-soft p-6" data-testid="settings-profile">
        <h2 className="font-heading text-lg font-medium text-[#2C302E]">Profile</h2>
        <div className="mt-3 text-sm text-[#59605C] space-y-1">
          <div>Name: <span className="text-[#2C302E]">{user?.full_name || "—"}</span></div>
          <div>Email: <span className="text-[#2C302E]">{user?.email}</span></div>
          <div>Sign-in method: <span className="text-[#2C302E]">{user?.auth_provider === "google" ? "Google" : "Email & password"}</span></div>
        </div>
      </div>

      {/* Pointer to the new Accounts page */}
      <Link to="/app/accounts" className="card-soft p-6 flex items-start justify-between gap-4 hover:bg-[#F9F8F6] transition-colors" data-testid="settings-accounts-link">
        <div>
          <h2 className="font-heading text-lg font-medium text-[#2C302E]">Money accounts</h2>
          <p className="text-sm text-[#59605C] mt-1">Manage your bank accounts, cash on hand, and credit cards. Your total available cash is auto-calculated from the balances here.</p>
        </div>
        <ArrowRight className="text-[#4A6148] shrink-0 mt-1" size={20} />
      </Link>

      {loading ? <div className="text-[#59605C]">Loading…</div> : (
        <>
          <form className="card-soft p-6" onSubmit={saveThresholds} data-testid="settings-thresholds-form">
            <h2 className="font-heading text-lg font-medium text-[#2C302E]">Safe / Tight / Short cushion thresholds</h2>
            <p className="text-sm text-[#59605C] mt-1 leading-relaxed">
              The Dashboard labels each pay period and end-of-month projection as <span className="font-semibold text-[#3B5B3E]">Safe</span>, <span className="font-semibold text-[#8C5D30]">Tight</span>, or <span className="font-semibold text-[#8C3A30]">Short</span>. Set the dollar cutoffs below to match how much cushion feels safe for you. Leave blank to use the app defaults ($100 / $0).
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#59605C]">Tight cushion ($)</span>
                <input
                  type="number"
                  step="0.01"
                  placeholder="100.00 (default)"
                  className="input-soft mt-1"
                  value={thresholds.cushion_tight}
                  onChange={(e) => setThresholds({ ...thresholds, cushion_tight: e.target.value })}
                  data-testid="settings-cushion-tight"
                />
                <span className="block text-xs text-[#8A918D] mt-1">Leftover below this = Tight. Currently: ${effectiveThresholds.effective_tight}</span>
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#59605C]">Short cushion ($)</span>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00 (default)"
                  className="input-soft mt-1"
                  value={thresholds.cushion_short}
                  onChange={(e) => setThresholds({ ...thresholds, cushion_short: e.target.value })}
                  data-testid="settings-cushion-short"
                />
                <span className="block text-xs text-[#8A918D] mt-1">Leftover below this = Short. Currently: ${effectiveThresholds.effective_short}</span>
              </label>
            </div>
            <div className="mt-4 flex justify-end">
              <button className="btn-primary" disabled={savingThresholds} data-testid="settings-save-thresholds">
                {savingThresholds ? "Saving…" : "Save thresholds"}
              </button>
            </div>
          </form>

          <form className="card-soft p-6" onSubmit={saveNotes} data-testid="settings-notes-form">
            <h2 className="font-heading text-lg font-medium text-[#2C302E]">Notes</h2>
            <p className="text-sm text-[#59605C] mt-1">Free-form notes for yourself — reminders, thresholds, anything.</p>
            <input className="input-soft mt-3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes…" data-testid="settings-notes-input" />
            <div className="mt-4 flex justify-end">
              <button className="btn-primary" disabled={saving} data-testid="settings-save-notes">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>

          <div className="card-soft p-6" data-testid="settings-monthly-summary">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h2 className="font-heading text-lg font-medium text-[#2C302E]">Monthly email summary</h2>
                <p className="text-sm text-[#59605C] mt-1 max-w-xl">
                  Get a friendly recap on the 1st of every month: paychecks, unpaid bills, past-due count, and closing balance. Off by default.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 select-none cursor-pointer">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-[#4A6148]"
                  checked={monthlySummaryOptIn}
                  disabled={savingMonthlySummary}
                  onChange={(e) => saveMonthlySummary(e.target.checked)}
                  data-testid="settings-monthly-summary-toggle"
                />
                <span className="text-sm font-medium text-[#2C302E]">
                  {monthlySummaryOptIn ? "Subscribed" : "Not subscribed"}
                </span>
              </label>
            </div>
            {monthlySummaryOptIn && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <label className="text-xs font-semibold uppercase text-[#59605C]">Send to</label>
                <input
                  className="input-soft flex-1 min-w-[240px]"
                  type="email"
                  value={monthlySummaryEmail}
                  onChange={(e) => setMonthlySummaryEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="settings-monthly-summary-email"
                />
                <button
                  className="btn-secondary"
                  disabled={savingMonthlySummary}
                  onClick={() => saveMonthlySummary(true)}
                  data-testid="settings-monthly-summary-save-email"
                >
                  Update email
                </button>
              </div>
            )}
            <div className="mt-3 text-[11px] text-[#8A918D]">
              Delivery isn&rsquo;t live yet — we&rsquo;re saving your preference now so you&rsquo;re first on the list when the email service is wired up.
            </div>
          </div>

          <div className="card-soft p-6" data-testid="settings-weekly-reminder">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h2 className="font-heading text-lg font-medium text-[#2C302E]">Weekly balance reminder</h2>
                <p className="text-sm text-[#59605C] mt-1 max-w-xl">
                  A friendly Monday-morning check-in email — the 3-step recap, bills paid last week, bills still due in the current pay period, and your current Real Balance. Great for keeping your streak alive. Off by default.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 select-none cursor-pointer">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-[#4A6148]"
                  checked={weeklyReminderOptIn}
                  disabled={savingWeeklyReminder}
                  onChange={(e) => saveWeeklyReminder(e.target.checked)}
                  data-testid="settings-weekly-reminder-toggle"
                />
                <span className="text-sm font-medium text-[#2C302E]">
                  {weeklyReminderOptIn ? "On · every Monday" : "Off"}
                </span>
              </label>
            </div>
            <div className="mt-3 text-[11px] text-[#8A918D]">
              Delivered around 8am Central US time. Every email includes a one-click link to turn reminders back off if you change your mind.
            </div>
          </div>

          <div className="card-soft p-6" data-testid="settings-autoshare">
            <div className="min-w-0">
              <h2 className="font-heading text-lg font-medium text-[#2C302E]">Snapshot autoshare</h2>
              <p className="text-sm text-[#59605C] mt-1 max-w-xl">
                Save an email address for the Dashboard&rsquo;s one-tap <strong>Email snapshot</strong> button. Sends a short recap of the current pay period (or month) with the numbers inline and a link your partner can open in a browser — no login needed.
              </p>
            </div>
            <form onSubmit={saveAutoshareEmail} className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
              <label className="flex-1 min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#59605C] mb-1">Send snapshots to</span>
                <input
                  type="email"
                  className="input-soft w-full"
                  placeholder={user?.email || "partner@example.com"}
                  value={autoshareEmail}
                  onChange={(e) => setAutoshareEmail(e.target.value)}
                  data-testid="settings-autoshare-email-input"
                />
              </label>
              <button
                type="submit"
                className="btn-primary shrink-0"
                disabled={savingAutoshareEmail}
                data-testid="settings-autoshare-save"
              >
                {savingAutoshareEmail ? "Saving…" : "Save"}
              </button>
            </form>
            <div className="mt-3 text-[11px] text-[#8A918D]">
              Snapshots are read-only public links that auto-expire after 30 days. Leave blank to be prompted every time.
            </div>
          </div>

          {/* Snapshot follow-up — 7-day personal check-in email to the user
              after they share a tight-week snapshot. Encourages users to
              log back in and reconcile so forecasting becomes a habit. */}
          <div className="card-soft p-6" data-testid="settings-followup">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h2 className="font-heading text-lg font-medium text-[#2C302E]">Snapshot follow-ups</h2>
                <p className="text-sm text-[#59605C] mt-1 max-w-xl">
                  A week after you share a tight-week snapshot, we&rsquo;ll send you a warm check-in — celebratory if you kept up with your streak, gentle nudge if not. One email per snapshot, never any ongoing series.
                </p>
              </div>
              <label className="inline-flex items-center gap-3 shrink-0 cursor-pointer" data-testid="settings-followup-toggle-label">
                <span className="text-xs font-semibold text-[#59605C]">
                  {followupOptIn ? "On" : "Off"}
                </span>
                <span className="relative inline-block w-11 h-6">
                  <input
                    type="checkbox"
                    checked={followupOptIn}
                    disabled={savingFollowup}
                    onChange={(e) => saveFollowupOptIn(e.target.checked)}
                    className="sr-only peer"
                    data-testid="settings-followup-toggle"
                  />
                  <span className="absolute inset-0 rounded-full bg-[#D8CFB9] peer-checked:bg-[#4A6148] transition-colors" />
                  <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                </span>
              </label>
            </div>
            <form
              onSubmit={saveFollowupThreshold}
              className={`mt-4 flex flex-col sm:flex-row gap-3 sm:items-end ${followupOptIn ? "" : "opacity-50 pointer-events-none"}`}
            >
              <label className="flex-1 min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#59605C] mb-1">
                  Trigger when projected end-of-period is at or below
                </span>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A918D] text-sm pointer-events-none">$</span>
                  <input
                    type="number"
                    step="10"
                    className="input-soft w-full pl-6"
                    value={followupThreshold}
                    onChange={(e) => setFollowupThreshold(e.target.value)}
                    disabled={!followupOptIn || savingFollowup}
                    data-testid="settings-followup-threshold-input"
                  />
                </div>
                <div className="mt-1 text-[11px] text-[#8A918D]">
                  Default $0 (only truly negative forecasts). Raise it to catch close-call weeks too.
                </div>
              </label>
              <button
                type="submit"
                className="btn-primary shrink-0"
                disabled={!followupOptIn || savingFollowup}
                data-testid="settings-followup-save"
              >
                {savingFollowup ? "Saving…" : "Save threshold"}
              </button>
            </form>
          </div>

          {/* General Spending low-balance nudge threshold (Feb 2026 D3).
              Powers the Step 1 nudge when the user's General Spending
              accounts fall at or below the number they set here. */}
          <div className="card-soft p-6" data-testid="settings-gs-threshold">
            <h2 className="font-heading text-lg font-medium text-[#2C302E]">General Spending nudge</h2>
            <p className="text-sm text-[#59605C] mt-1 max-w-xl">
              A soft nudge appears on your Dashboard when the combined balance of your General Spending accounts is at or below this number. Set to 0 to turn off.
            </p>
            <form onSubmit={saveGsThreshold} className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
              <label className="flex-1 min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#59605C] mb-1">
                  Warn me when General Spending drops to
                </span>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A918D] text-sm pointer-events-none">$</span>
                  <input
                    type="number"
                    step="10"
                    min="0"
                    className="input-soft w-full pl-6"
                    value={gsThreshold}
                    onChange={(e) => setGsThreshold(e.target.value)}
                    disabled={savingGs}
                    data-testid="settings-gs-threshold-input"
                  />
                </div>
                <div className="mt-1 text-[11px] text-[#8A918D]">
                  Default $100. Raise it to be nudged earlier, lower it (or 0) to only see the warning when things are really tight.
                </div>
              </label>
              <button
                type="submit"
                className="btn-primary shrink-0"
                disabled={savingGs}
                data-testid="settings-gs-threshold-save"
              >
                {savingGs ? "Saving…" : "Save threshold"}
              </button>
            </form>
          </div>

          <div className="card-soft p-6" data-testid="settings-demo-block">
            <h2 className="font-heading text-lg font-medium text-[#2C302E]">Sample demo data</h2>
            <p className="text-sm text-[#59605C] mt-1">
              Explore the app with a realistic pretend household: 2 bank accounts + cash, common bills, prior-month carryover, and mixed income. Great when you want to poke around before entering your own numbers.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                className="btn-secondary"
                onClick={() => setShowDemoModal(true)}
                disabled={demoLoading}
                data-testid="settings-load-demo"
              >
                <Database size={16} /> Load sample data
              </button>
              <button
                className="btn-ghost !text-[#8C3A30] hover:!bg-[#FDF2F0]"
                onClick={() => setShowClearDemoModal(true)}
                disabled={clearingDemo || demoStatus.total === 0}
                data-testid="settings-clear-demo"
                title={demoStatus.total === 0
                  ? "No sample data left to clear — everything is either yours or already removed."
                  : `Removes ${demoStatus.total} untouched sample row${demoStatus.total === 1 ? "" : "s"}.`}
              >
                <Trash2 size={14} /> {demoStatus.total === 0 ? "No sample data to clear" : `Clear sample data (${demoStatus.total})`}
              </button>
            </div>
            {demoStatus.total > 0 && (
              <div className="mt-3 text-[11px] text-[#8A918D] leading-relaxed">
                Only removes rows you haven&rsquo;t edited. Anything you&rsquo;ve changed or added stays safe.
              </div>
            )}
          </div>
        </>
      )}

      {showDemoModal && (
        <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={() => !demoLoading && setShowDemoModal(false)} data-testid="demo-confirm-modal">
          <div className="card-soft p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-medium">Replace your data with demo data?</h3>
              <button className="btn-ghost !p-2" onClick={() => setShowDemoModal(false)} disabled={demoLoading} aria-label="Close"><X size={16} /></button>
            </div>
            <p className="text-sm text-[#59605C]">
              This wipes your existing bills, income, funding sources, and pending payments, and reseeds with a made-up household. Your login stays the same. You can&rsquo;t undo this — but you can always re-run it, or clear the sample data later once you&rsquo;ve added your own.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowDemoModal(false)} disabled={demoLoading}>Cancel</button>
              <button className="btn-primary" onClick={loadDemo} disabled={demoLoading} data-testid="settings-load-demo-confirm">
                {demoLoading ? "Loading…" : "Yes, load demo data"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearDemoModal && (
        <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={() => !clearingDemo && setShowClearDemoModal(false)} data-testid="clear-demo-modal">
          <div className="card-soft p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-medium">Clear sample data?</h3>
              <button className="btn-ghost !p-2" onClick={() => setShowClearDemoModal(false)} disabled={clearingDemo} aria-label="Close"><X size={16} /></button>
            </div>
            <p className="text-sm text-[#59605C] leading-relaxed">
              We&rsquo;ll delete the <strong className="text-[#2C302E]">{demoStatus.total} sample row{demoStatus.total === 1 ? "" : "s"}</strong> you haven&rsquo;t edited. Anything you&rsquo;ve changed or added is treated as yours and stays safe.
            </p>
            <p className="text-xs text-[#8A918D] mt-2">This can&rsquo;t be undone, but you can reload sample data at any time from this same page.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowClearDemoModal(false)} disabled={clearingDemo}>Cancel</button>
              <button
                className="btn-primary !bg-[#8C3A30] hover:!bg-[#7A2E24]"
                onClick={clearDemoData}
                disabled={clearingDemo}
                data-testid="settings-clear-demo-confirm"
              >
                {clearingDemo ? "Clearing…" : "Yes, clear sample data"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
